/**
 * Reading the closure register for the map and the boards.
 *
 * The insight screens read closures inside their own queries, counting what
 * happened. The map needs something else: what is in force, what is
 * scheduled, and what was lifted lately — including closures that have not
 * started yet. Every key starts `["closures", siteId]`, so a write refreshes
 * all of them at once.
 */
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../app/SessionContext";
import { DataSourceError } from "./dataSource";

/** How far back the map shows a closure that has ended. */
export const MAP_CLOSURE_DAYS = 7;

export const closureKeys = {
  /** everything about this site's closures — what a write invalidates */
  site: (siteId: string) => ["closures", siteId] as const,
  map: (siteId: string) => ["closures", siteId, "map"] as const,
};

/**
 * In force, scheduled, and ended in the last week — what the map draws.
 *
 * `enabled` lets the caller hold the read until something will use it (the
 * boards' engine has loaded), so a page with no board never asks.
 */
export function useMapClosures(opts: { enabled?: boolean } = {}) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: closureKeys.map(site.id),
    queryFn: () =>
      source.listClosures(site.id, new Date(Date.now() - MAP_CLOSURE_DAYS * 86_400_000).toISOString(), {
        includePlanned: true,
      }),
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    /* a refusal will not change on a second asking */
    retry: (n, e) => !(e instanceof DataSourceError && e.status !== undefined && e.status < 500) && n < 1,
  });
}
